import React, { useCallback, useMemo } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import Promise from 'bluebird';
import classnames from 'classnames';
import { createUseStyles } from 'react-jss';
import { AddressUtils } from '@bingads-webui-clientcenter/address';
import { AddressForm } from '@bingads-webui-cc-react/address-form';
import { LineByLineFormGroup } from '@bingads-webui-cc-react/layout';
import { BusinessAddressSelector, CountrySelector } from '../controls';
import { Forms, addressFormProperties, AddressSelectorOptions } from '../constants';
import { AddressPropTypes, PassiveErrorsShapes, FormControlsShape, helpPopupIdsShape } from '../shapes';
import { getSelectedABL } from '../utils';
import { getBaseABLStyles } from '../styles';

const { twoLinesFormat } = AddressUtils;

const useStyles = createUseStyles(getBaseABLStyles());

export const AddressFormWrapper = ({
  i18n,
  formControls,
  countryCode,
  countryList,
  provinceList,
  getStatesForCountryCode,
  disableCurrencyAutoSelect,
  onCountryChange,
  handlePrefillAddressChange,
  alwaysShowError,
  address,
  addressList,
  mtPrefillAddressId,
  prefillAddressId,
  onAddressChange,
  isReadonly,
  passiveErrors,
  helpPopupIds,
  isSignupFlow,
  isEnhancedABLP1Enabled,
  disableAddressDelete,
  showRadioGroupInBusinessAddressSelector,
  onlyIncludeBusinessAIVAddresses,
  showEmptyBusinessAIVAddresses,
}) => {
  const classes = useStyles();
  const countryName = useMemo(() => _.get(_.findWhere(countryList, { Code: countryCode }), 'Name'), [countryCode, countryList]);
  // LocationAndCurrency pilot: when the country is editable, resolve the state
  // list on demand for the country AddressForm requests. This avoids a race
  // where the prefetched `provinceList` is momentarily empty during a country
  // change and AddressForm captures the stale (empty) list. When the pilot is
  // off, keep the original behavior of resolving the prefetched list.
  const findStates = useCallback(
    requestedCountryCode => (disableCurrencyAutoSelect && _.isFunction(getStatesForCountryCode)
      ? getStatesForCountryCode(requestedCountryCode)
      : Promise.resolve(provinceList)),
    [disableCurrencyAutoSelect, getStatesForCountryCode, provinceList]
  );
  const { selectedAddress: prefillAddress } = useMemo(() => getSelectedABL({
    addressId: prefillAddressId,
    addressList,
    countryCode,
  }), [addressList, countryCode, prefillAddressId]);

  if (isReadonly) {
    return (
      <LineByLineFormGroup
        contentColClass={classnames('col-xs-6', 'business-location-control')}
        helpPopupTopic={isSignupFlow ? 'Pop_BA_Signup_BusinessLocationTaxInfo' : 'Pop_BA_Accounts_BusinessLocation'}
        label={i18n.getString(_TL_('Business location'))}
        id="account-business-address-static-form"
        className="account-business-address-static-form"
        isStatic
      >
        {twoLinesFormat(address).join('\n') || i18n.getString(_TL_('(Not set)'))}
      </LineByLineFormGroup>
    );
  }

  return (
    <div className={classnames('account-business-address-form-wrapper')}>
      {formControls[Forms.CountrySelector].visible && (
        <CountrySelector
          i18n={i18n}
          countryCode={countryCode}
          countryList={countryList}
          onCountryChange={onCountryChange}
          alwaysShowError={alwaysShowError}
          helpPopupId={helpPopupIds[Forms.CountrySelector].popupId}
          helpPopupAriaLabel={helpPopupIds[Forms.CountrySelector].ariaLabel}
          isSignupFlow={isSignupFlow}
          isOptional={formControls[Forms.CountrySelector].optional}
          disabled={formControls[Forms.CountrySelector].disabled}
          showEmptyBusinessAIVAddresses={showEmptyBusinessAIVAddresses}
          onlyIncludeBusinessAIVAddresses={onlyIncludeBusinessAIVAddresses}
        />)
      }
      {
        formControls[Forms.BusinessAddressSelector].visible && (
          <BusinessAddressSelector
            i18n={i18n}
            mtPrefillAddressId={mtPrefillAddressId}
            prefillAddressId={prefillAddressId}
            addressList={addressList}
            countryCode={countryCode}
            handlePrefillAddressChange={handlePrefillAddressChange}
            label={formControls[Forms.BusinessAddressSelector].label}
            helpPopupAriaLabel={helpPopupIds[Forms.BusinessAddressSelector].ariaLabel}
            isSignupFlow={isSignupFlow}
            isOptional={formControls[Forms.BusinessAddressSelector].optional}
            disabled={formControls[Forms.BusinessAddressSelector].disabled}
            isEnhancedABLP1Enabled={isEnhancedABLP1Enabled}
            disableAddressDelete={disableAddressDelete}
            showRadioGroupInBusinessAddressSelector={showRadioGroupInBusinessAddressSelector}
            onlyIncludeBusinessAIVAddresses={onlyIncludeBusinessAIVAddresses}
            showEmptyBusinessAIVAddresses={showEmptyBusinessAIVAddresses}
          />)
      }
      {
        formControls[Forms.AddressForm].visible && (
          <LineByLineFormGroup showLabel={false}>
            <AddressForm
              id="address-form"
              className={classes.addressForm}
              i18n={i18n}
              findStates={findStates}
              countries={[{ Code: countryCode, Name: countryName }]}
              model={_.pick(address, addressFormProperties)}
              prefillModel={prefillAddress}
              onAddressChange={onAddressChange}
              showFieldsLabel
              showSectionLabel
              alwaysShowError={alwaysShowError}
              hideSingleCountry
              useDropdownList
              showPlaceholder
              useModelAsState
              passiveErrors={passiveErrors.AddressForm}
              isSignupFlow={isSignupFlow}
              isOptional={formControls[Forms.AddressForm].optional}
            />
          </LineByLineFormGroup>)
      }
    </div>
  );
};

AddressFormWrapper.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  formControls: FormControlsShape.isRequired,
  countryCode: PropTypes.string,
  countryList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  provinceList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  getStatesForCountryCode: PropTypes.func,
  disableCurrencyAutoSelect: PropTypes.bool,
  onCountryChange: PropTypes.func.isRequired,
  handlePrefillAddressChange: PropTypes.func.isRequired,
  alwaysShowError: PropTypes.bool.isRequired,
  address: AddressPropTypes,
  addressList: PropTypes.arrayOf(PropTypes.any).isRequired,
  mtPrefillAddressId: PropTypes.number,
  prefillAddressId: PropTypes.number,
  onAddressChange: PropTypes.func.isRequired,
  isReadonly: PropTypes.bool.isRequired,
  passiveErrors: PassiveErrorsShapes,
  helpPopupIds: helpPopupIdsShape,
  isSignupFlow: PropTypes.bool,
  isEnhancedABLP1Enabled: PropTypes.bool,
  disableAddressDelete: PropTypes.bool,
  showRadioGroupInBusinessAddressSelector: PropTypes.bool,
  onlyIncludeBusinessAIVAddresses: PropTypes.bool,
  showEmptyBusinessAIVAddresses: PropTypes.bool,
};

AddressFormWrapper.defaultProps = {
  countryCode: null,
  getStatesForCountryCode: null,
  disableCurrencyAutoSelect: false,
  mtPrefillAddressId: AddressSelectorOptions.enterNewAddressId,
  prefillAddressId: AddressSelectorOptions.enterNewAddressId,
  address: {},
  passiveErrors: {},
  helpPopupIds: {},
  isSignupFlow: false,
  isEnhancedABLP1Enabled: false,
  disableAddressDelete: false,
  showRadioGroupInBusinessAddressSelector: false,
  onlyIncludeBusinessAIVAddresses: false,
  showEmptyBusinessAIVAddresses: false,
};
